import React, { useEffect, useState } from 'react'

import { useHistory, useParams } from 'react-router-dom'

import Button from '@components/button/button.component'
import EngagementDetailHeader from '@components/engagement-detail-header/engagement-detail-header.component'
import Label from '@components/form-label/form-label.component'
import ResponsiveContainer from '@components/responsive-container/responsive-container.component'
import Switch from '@components/form-switch/form-switch.component'
import TextareaAutosize from 'react-autosize-textarea'
import Icon from '@components/icon/icon.component'

import { useDispatch, useSelector } from 'react-redux'
import { selectAccountId, selectApiKey, selectHash, makeSelectIsLoadingType } from '@selectors'

import i18n from 'i18n-js'

import useStyles from './engage-send.style'
import { CTA_SUBMIT, SET_ENGAGEMENT_DETAIL_ID, SET_REPLY_GUID, ON_CTA_SUBMIT } from '@actions/types'
import { BUTTON_CANCEL, PLACEHOLDER_RESPONSE } from '../../assets/i18n/strings'

const selectIsSubmittingCta = makeSelectIsLoadingType(CTA_SUBMIT)

const INITIAL_STATE = {
  suggestionsQuestion: '',
  replies: [],
  location: false
}

const EngagementSend = () => {
  const classes = useStyles()

  const dispatch = useDispatch()

  const history = useHistory()

  const { engagementId, replyGuid } = useParams()

  const [form, setForm] = useState({ ...INITIAL_STATE })

  const {
    suggestionsQuestion,
    replies,
    location
  } = form

  const apiKey = useSelector(selectApiKey)
  const hash = useSelector(selectHash)
  const auth = { apiKey, hash }

  const accountId = useSelector(selectAccountId)

  const engageRef = `engage/${accountId}/cta`

  const engagements = useSelector(
    ({ firestore: { ordered } }) => ordered[engageRef] && ordered[engageRef]
  )

  const engagement = engagements?.find(e => e.id === engagementId)

  useEffect(() => {
    if (engagement) {
      dispatch({ type: SET_ENGAGEMENT_DETAIL_ID, payload: engagementId })
    }
  }, [dispatch, engagement, engagementId])

  useEffect(() => {
    if (replyGuid) {
      dispatch({ type: SET_REPLY_GUID, payload: replyGuid })
    }
  }, [dispatch, replyGuid])

  const isSubmittingCta = useSelector(selectIsSubmittingCta)

  if (!engagement) return null

  const { suggestions, groups, message_text: msgText, no_sent: noSent } = engagement

  const selectedReply = suggestions.find(suggestion => suggestion.reply_guid === replyGuid)

  const { users = [], text, type } = selectedReply

  const noNoResponse = () => {
    let totalResponses = 0

    suggestions.forEach(suggestion => {
      const { no_responses: noResponses } = suggestion

      if (noResponses) totalResponses = totalResponses + noResponses
    })

    return noSent - totalResponses
  }

  const sendCTAData = () => {
    const excludeGroups = []
    const excludeUsers = []
    let includeGroups = []

    if (type === 'no-response') {
      suggestions.forEach(suggestion => {
        const { users: suggestionUsers = [], group_id: groupId } = suggestion

        if (groupId) {
          excludeGroups.push(groupId)
        } else {
          suggestionUsers.forEach(user => {
            excludeUsers.push(user.id)
          })
        }
      })

      includeGroups = groups
    }

    return {
      account_id: accountId,
      exclude_groups: excludeGroups,
      exclude_users: excludeUsers,
      include_groups: includeGroups,
      message_id: engagementId,
      reply_guid: replyGuid,
      users: users?.map(user => user.id) || []
    }
  }
  const handleInputChange = (e, index) => {
    let { name, value, checked, type } = e.target

    if (type === 'checkbox') value = checked

    if (name.startsWith('reply')) {
      const repliesToUpdate = [...replies]

      repliesToUpdate[index] = { text: value }

      setForm({
        ...form,
        replies: repliesToUpdate
      })
    } else {
      setForm({
        ...form,
        [name]: value
      })
    }
  }

  const onSubmit = (e) => {
    e.preventDefault()

    const suggestionsReplies = replies.filter(({ text }) => text !== '')

    const data = {
      ...sendCTAData(),
      include_location: location,
      suggestion_question: suggestionsQuestion,
      suggestions_replies: suggestionsReplies
    }

    dispatch({ type: ON_CTA_SUBMIT, payload: { auth, data } })
  }

  const inputCount = replies.length + 1

  const buttonDisabled = () => {
    const noQuestion = suggestionsQuestion.length === 0
    const noReply = replies.length === 0

    return noQuestion || noReply
  }

  const handleOnCancel = () => {
    history.goBack()
  }

  const renderInputs = () => {
    const inputs = []

    for (let i = 0; i < inputCount; i++) {
      inputs.push(
        <div
          key={`input-${i}`}
          className='control is-expanded'
        >
          <TextareaAutosize
            className={`textarea ${classes.customTextarea}`}
            name={`reply-${i}`}
            onChange={e => handleInputChange(e, i)}
            placeholder={i18n.t(PLACEHOLDER_RESPONSE)}
            type='text'
            value={replies[i]?.text || ''}
          />
        </div>)
    }

    return inputs
  }

  return (
    <div className={classes.container}>
      <ResponsiveContainer className={classes.responsive}>
        {type === 'no-response' ? (
          <EngagementDetailHeader
            parentTitle={msgText}
            title='No Response'
            usersMessaged={`${noNoResponse()} ${noNoResponse() === 1 ? 'User' : 'Users'} will receive message`}
          />
        ) : (
          <EngagementDetailHeader
            parentTitle={msgText}
            title={text}
            usersMessaged={`${noSent} ${noSent === 1 ? 'User' : 'Users'} will receive message`}
          />
        )}

      </ResponsiveContainer>
      <form
        className={classes.form}
        onSubmit={onSubmit}
      >
        <ResponsiveContainer className={classes.responsive}>
          <div className={classes.field}>
            <Label>Follow-Up Question</Label>
            <div className='control is-expanded'>
              <TextareaAutosize
                className={`textarea ${classes.customTextarea}`}
                name='suggestionsQuestion'
                onChange={handleInputChange}
                placeholder='What is the question?'
                type='text'
                value={suggestionsQuestion}
              />
            </div>
          </div>

          <div className={classes.field}>
            <Label>Responses</Label>
            {renderInputs()}
          </div>
          <div className={`${classes.field} ${classes.switch}`}>
            <div className={classes.iconTextContainer}>
              <Icon
                className={classes.pin}
                type='pin'
              />
              <div>Request responder location</div>
            </div>
            <Switch
              checked={location}
              className='switch is-rounded'
              id='includeLocation'
              name='location'
              onChange={handleInputChange}
              type='checkbox'
              value={location}
            />
          </div>
          <div className={classes.buttons}>
            <Button
              backgroundColor='white'
              className={`button ${classes.outline}`}
              onClick={handleOnCancel}
              type='button'
            >
              {i18n.t(BUTTON_CANCEL)}
            </Button>
            <Button
              className='button is-primary'
              disabled={buttonDisabled()}
              isLoading={isSubmittingCta}
              style={{ marginLeft: 8 }}
              type='submit'
            >
              Send Follow-Up
            </Button>
          </div>
        </ResponsiveContainer>
      </form>
    </div>
  )
}

export default EngagementSend
